import { HiOutlineChevronUpDown } from 'react-icons/hi2';
import { cn } from '../../../../utilities/cn';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { useState, useRef } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';

interface SubcategoryComboBoxProps
  extends React.HTMLAttributes<HTMLDivElement> {
  data: string[];
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  placeholder?: string;
}

export const SubcategoryComboBox: React.FC<SubcategoryComboBoxProps> = ({
  data,
  selected,
  setSelected,
  className,
  placeholder,
}) => {
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredData =
    query === ''
      ? data
      : data.filter((item) =>
          item
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        );

  const allSelected = selected.length === data.length;
  const hasPartialFilters = selected.length > 0 && !allSelected;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected([...data]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Combobox multiple value={selected} onChange={setSelected}>
        <div
          className={cn(
            'relative w-fit',
            hasPartialFilters && 'rounded-md ring-2 ring-blue-400',
          )}
        >
          <ComboboxInput
            className={cn(
              'w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-500',
              className,
            )}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={
              selected.length > 0
                ? `${selected.length}/${data.length} selected`
                : placeholder
            }
            displayValue={() => ''}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2">
            <HiOutlineChevronUpDown className="h-5 w-5" />
          </ComboboxButton>
        </div>

        <ComboboxOptions className="absolute z-50 mt-1 max-h-96 min-w-96 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5">
          <div className="mb-1 flex items-center justify-between border-b border-gray-300 bg-white p-2">
            <label className="flex cursor-pointer items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
                className="accent-blue-500"
              />
              Select All
            </label>
          </div>

          {filteredData.map((item) => {
            const isSelected = selected.includes(item);
            return (
              <ComboboxOption
                key={item}
                value={item}
                className={({ active }) =>
                  cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-left',
                    active && 'bg-blue-100',
                    isSelected && 'bg-blue-50',
                  )
                }
              >
                {() => (
                  <div className="flex w-full items-center gap-2">
                    <IoMdCheckmarkCircleOutline
                      className={cn(
                        'h-4 w-4',
                        isSelected ? 'text-blue-600' : 'invisible',
                      )}
                    />
                    {item}
                  </div>
                )}
              </ComboboxOption>
            );
          })}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
};
